/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/**
 * LoadingSpinner — Teamwork Graph branded loading indicator.
 *
 * A four-dot logo that bursts out from the center, then continuously
 * draws a multi-color "snake" between the dots, rotating between
 * patterns. Mirrors the public size API of `@atlaskit/spinner` so it
 * can be a drop-in replacement at existing call sites.
 *
 * The SVG is rendered once and updated imperatively from a
 * `requestAnimationFrame` loop to avoid re-rendering React 60 times/sec.
 */

import { useEffect, useMemo, useRef } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { usePrefersReducedMotion } from '../InteractiveHeroIcons/use-prefers-reduced-motion';

const styles = cssMap({
	wrapper: {
		display: 'inline-flex',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 0,
	},
});

/* -------------------------------------------------------------------------- */
/* Public types                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Public size scale. Mirrors `@atlaskit/spinner` so existing call sites
 * can swap implementations without changing props.
 */
export type LoadingSpinnerSize = 'small' | 'medium' | 'large' | 'xlarge';

export type LoadingSpinnerProps = {
	/**
	 * Visual size. Mirrors `@atlaskit/spinner`.
	 *
	 * @default 'medium'
	 */
	size?: LoadingSpinnerSize;
	/**
	 * Accessible label announced to assistive technology while the logo
	 * is spinning.
	 *
	 * @default 'Loading'
	 */
	label?: string;
	/** Optional test id applied to the wrapping element. */
	testId?: string;
};

/* -------------------------------------------------------------------------- */
/* Geometry, timing and patterns                                              */
/* -------------------------------------------------------------------------- */

const VIEWBOX_SIZE = 24;
const VIEWBOX_CENTER = VIEWBOX_SIZE / 2;

const SIZE_PX: Record<LoadingSpinnerSize, number> = {
	small: 16,
	medium: 24,
	large: 32,
	xlarge: 64,
};

const DOT_RADIUS = 2.5;
const NODE_STROKE_WIDTH = 1.5;
const SPREAD_RADIUS = 8;
const LINE_WIDTH = 1.5;

const BURST_DURATION_S = 0.4;
const ROTATE_DURATION_S = 0.4;
const DRAW_DURATION_S = 0.8;
/** Fraction of the snake duration after which the tail begins to retract. */
const TAIL_DELAY = 0.35;
const ROTATION_AMOUNT_DEG = 45;

/** Dot starting angles (radians). Order matters — patterns reference these indices. */
const DOT_ANGLES: readonly number[] = [
	-Math.PI / 2, // top
	0, // right
	Math.PI / 2, // bottom
	Math.PI, // left
];

type Segment = readonly [fromIdx: number, toIdx: number];

/** Snake patterns — one set of dot-to-dot segments per cycle. */
const DRAW_PATTERNS: readonly (readonly Segment[])[] = [
	[
		[3, 0],
		[0, 2],
		[2, 1],
	],
	[
		[0, 2],
		[2, 3],
		[3, 1],
	],
	[
		[3, 1],
		[1, 0],
		[0, 2],
	],
	[
		[0, 1],
		[1, 3],
		[3, 2],
	],
];

const MAX_LINE_COUNT = Math.max(...DRAW_PATTERNS.map((p) => p.length));

const TOTAL_SNAKE_DURATION_S = DRAW_DURATION_S / (1 - TAIL_DELAY);
const CYCLE_DURATION_S = TOTAL_SNAKE_DURATION_S + ROTATE_DURATION_S;
const ROTATION_AMOUNT_RAD = (ROTATION_AMOUNT_DEG * Math.PI) / 180;

/* -------------------------------------------------------------------------- */
/* Easing                                                                     */
/* -------------------------------------------------------------------------- */

const clamp01 = (t: number): number => Math.min(1, Math.max(0, t));

function easeOutBack(t: number, overshoot = 1.0): number {
	const c = clamp01(t);
	const c3 = overshoot + 1;
	return 1 + c3 * Math.pow(c - 1, 3) + overshoot * Math.pow(c - 1, 2);
}

function easeInOutQuad(t: number): number {
	const c = clamp01(t);
	return c < 0.5 ? 2 * c * c : 1 - Math.pow(-2 * c + 2, 2) / 2;
}

/* -------------------------------------------------------------------------- */
/* Colors                                                                     */
/* -------------------------------------------------------------------------- */

/** Brand colors for the four dot rings. Aligned with `DOT_ANGLES`. */
function getDotColors(): readonly string[] {
	return [
		token('color.icon.accent.orange', '#FCA700'),
		token('color.icon.accent.lime', '#6A9A23'),
		token('color.icon.accent.blue', '#1868DB'),
		token('color.icon.accent.purple', '#AF59E0'),
	];
}

/** Single neutral color for the snake / connector lines. No exact ADS token. */
function getLineColor(): string {
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	return '#6C6F77';
}

function getMaskColor(): string {
	return token('elevation.surface', '#FFFFFF');
}

/* -------------------------------------------------------------------------- */
/* Pure animation engine                                                      */
/* -------------------------------------------------------------------------- */

type DotPosition = { x: number; y: number };

type FrameState = {
	positions: readonly DotPosition[];
	/** Snake head fraction in `[0, 1]`. Negative = no snake this frame. */
	drawFrac: number;
	/** Snake tail fraction in `[0, 1]`. */
	retractFrac: number;
	/** Index into `DRAW_PATTERNS`. */
	patternIndex: number;
};

function getDotPositions(spread: number, rotation: number): DotPosition[] {
	return DOT_ANGLES.map((angle) => ({
		x: VIEWBOX_CENTER + Math.cos(angle + rotation) * spread,
		y: VIEWBOX_CENTER + Math.sin(angle + rotation) * spread,
	}));
}

/**
 * Compute the frame at `elapsedSeconds` since animation start.
 *
 * Phase 1 (`< BURST_DURATION_S`): dots fly out from center with overshoot.
 * Phase 2 (repeating): snake draws + retracts, then logo rotates and
 * advances to the next pattern.
 */
function computeFrame(elapsedSeconds: number): FrameState {
	let spread = SPREAD_RADIUS;
	let rotation = 0;
	let drawFrac = -1;
	let retractFrac = 0;
	let patternIndex = 0;

	if (elapsedSeconds < BURST_DURATION_S) {
		const progress = elapsedSeconds / BURST_DURATION_S;
		spread = easeOutBack(progress, 0.8) * SPREAD_RADIUS;
	} else {
		const cycleElapsed = elapsedSeconds - BURST_DURATION_S;
		const cycleCount = Math.floor(cycleElapsed / CYCLE_DURATION_S);
		const cycleTime = cycleElapsed % CYCLE_DURATION_S;

		patternIndex = cycleCount % DRAW_PATTERNS.length;
		const baseRotation = cycleCount * ROTATION_AMOUNT_RAD;

		if (cycleTime < TOTAL_SNAKE_DURATION_S) {
			const snakeProgress = cycleTime / TOTAL_SNAKE_DURATION_S;
			drawFrac = easeInOutQuad(Math.min(1, snakeProgress / (1 - TAIL_DELAY)));
			const tailRaw = Math.max(0, (snakeProgress - TAIL_DELAY) / (1 - TAIL_DELAY));
			retractFrac = easeInOutQuad(Math.min(1, tailRaw));
			rotation = baseRotation;
		} else {
			const rotateProgress = (cycleTime - TOTAL_SNAKE_DURATION_S) / ROTATE_DURATION_S;
			rotation = baseRotation + easeOutBack(rotateProgress, 0.6) * ROTATION_AMOUNT_RAD;
		}
	}

	return {
		positions: getDotPositions(spread, rotation),
		drawFrac,
		retractFrac,
		patternIndex,
	};
}

/** Static frame used for `prefers-reduced-motion: reduce`. */
function computeStaticFrame(): FrameState {
	return {
		positions: getDotPositions(SPREAD_RADIUS, 0),
		drawFrac: -1,
		retractFrac: 0,
		patternIndex: 0,
	};
}

/* -------------------------------------------------------------------------- */
/* Imperative DOM updaters                                                    */
/* -------------------------------------------------------------------------- */

const HIDDEN_DASH = '0 9999';
const EPSILON = 0.001;

function hideLine(el: SVGPathElement): void {
	el.setAttribute('stroke-dasharray', HIDDEN_DASH);
	el.setAttribute('stroke-dashoffset', '0');
}

function updateDots(svg: SVGSVGElement, positions: readonly DotPosition[]): void {
	const circles = svg.querySelectorAll<SVGCircleElement>('[data-type="dot"], [data-type="mask"]');
	circles.forEach((circle) => {
		const idx = Number(circle.getAttribute('data-idx'));
		const pos = positions[idx];
		if (!pos) return;
		circle.setAttribute('cx', String(pos.x));
		circle.setAttribute('cy', String(pos.y));
	});
}

function updateLines(
	svg: SVGSVGElement,
	positions: readonly DotPosition[],
	pattern: readonly Segment[],
	drawFrac: number,
	retractFrac: number,
): void {
	const segLengths: number[] = [];
	const cumDist: number[] = [0];
	for (let i = 0; i < pattern.length; i++) {
		const from = positions[pattern[i][0]];
		const to = positions[pattern[i][1]];
		const dx = to.x - from.x;
		const dy = to.y - from.y;
		const len = Math.sqrt(dx * dx + dy * dy);
		segLengths.push(len);
		cumDist.push(cumDist[i] + len);
	}
	const totalLen = cumDist[pattern.length];
	const headDist = drawFrac * totalLen;
	const tailDist = retractFrac * totalLen;

	for (let i = 0; i < MAX_LINE_COUNT; i++) {
		const el = svg.getElementById(`line-${i}`) as SVGPathElement | null;
		if (!el) continue;

		const isInactive = i >= pattern.length || drawFrac < 0;
		if (isInactive) {
			hideLine(el);
			continue;
		}

		const [fromIdx, toIdx] = pattern[i];
		const from = positions[fromIdx];
		const to = positions[toIdx];
		const segLen = segLengths[i];
		const segCumStart = cumDist[i];

		const headPos = Math.min(segLen, Math.max(0, headDist - segCumStart));
		const tailPos = Math.min(segLen, Math.max(0, tailDist - segCumStart));
		const visibleLen = headPos - tailPos;

		if (visibleLen <= EPSILON || segLen < EPSILON) {
			hideLine(el);
			continue;
		}

		const gapLen = segLen - visibleLen;
		el.setAttribute('d', `M${from.x},${from.y} L${to.x},${to.y}`);
		el.setAttribute('stroke-dasharray', `${visibleLen.toFixed(4)} ${gapLen.toFixed(4)}`);
		el.setAttribute('stroke-dashoffset', (-tailPos).toFixed(4));
	}
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

/** Visiting order for masks/dots — preserves the original stacking. */
const DOT_ORDER = [0, 1, 3, 2] as const;

export function LoadingSpinner({
	size = 'medium',
	label = 'Loading',
	testId,
}: LoadingSpinnerProps): JSX.Element {
	const sizePx = SIZE_PX[size];
	const reducedMotion = usePrefersReducedMotion();

	// Tokens flip with `data-theme`; resolve once per render.
	const dotColors = useMemo(() => getDotColors(), []);
	const lineColor = useMemo(() => getLineColor(), []);
	const maskColor = useMemo(() => getMaskColor(), []);

	const svgRef = useRef<SVGSVGElement>(null);

	useEffect(() => {
		const svg = svgRef.current;
		if (!svg) return;

		const applyFrame = ({ positions, drawFrac, retractFrac, patternIndex }: FrameState) => {
			updateDots(svg, positions);
			updateLines(svg, positions, DRAW_PATTERNS[patternIndex], drawFrac, retractFrac);
		};

		if (reducedMotion) {
			applyFrame(computeStaticFrame());
			return;
		}

		let rafId = 0;
		let startTime = 0;

		const tick = (timestamp: number) => {
			if (!startTime) startTime = timestamp;
			const elapsedSeconds = (timestamp - startTime) / 1000;
			applyFrame(computeFrame(elapsedSeconds));
			rafId = requestAnimationFrame(tick);
		};

		rafId = requestAnimationFrame(tick);

		return () => {
			cancelAnimationFrame(rafId);
		};
	}, [reducedMotion]);

	return (
		<div role="status" aria-label={label} data-testid={testId} css={styles.wrapper}>
			<svg
				ref={svgRef}
				width={sizePx}
				height={sizePx}
				viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
				fill="none"
				aria-hidden="true"
				focusable="false"
			>
				{/* Snake / connector lines. One <path> per segment. */}
				<g>
					{Array.from({ length: MAX_LINE_COUNT }, (_, i) => (
						<path
							key={`line-${i}`}
							id={`line-${i}`}
							stroke={lineColor}
							strokeWidth={LINE_WIDTH}
							strokeLinecap="butt"
							fill="none"
							strokeDasharray={HIDDEN_DASH}
						/>
					))}
				</g>

				{/* Background masks — punch a hole under each dot. */}
				<g>
					{DOT_ORDER.map((i) => (
						<circle
							key={`mask-${i}`}
							data-type="mask"
							data-idx={i}
							cx={VIEWBOX_CENTER}
							cy={VIEWBOX_CENTER}
							r={DOT_RADIUS}
							fill={maskColor}
							stroke="none"
						/>
					))}
				</g>

				{/* Dot rings — drawn in brand color, on top of the masks. */}
				<g>
					{DOT_ORDER.map((i) => (
						<circle
							key={`dot-${i}`}
							data-type="dot"
							data-idx={i}
							cx={VIEWBOX_CENTER}
							cy={VIEWBOX_CENTER}
							r={DOT_RADIUS}
							fill="none"
							stroke={dotColors[i]}
							strokeWidth={NODE_STROKE_WIDTH}
						/>
					))}
				</g>
			</svg>
		</div>
	);
}

export default LoadingSpinner;
